import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@massilia/ui';
import { Button } from '@massilia/ui';
import { Calendar } from '@massilia/ui';
import { FullScreenOverlay } from '@massilia/ui';
import { MobileScrollCalendar } from '@massilia/ui';
import { OptionalLabel } from '@massilia/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@massilia/ui';
import { useIsMobile } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import React from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { FieldError } from './field-error';
export default function CalendarField({ className, captionLayout, dateFormat = 'dd LLL', disabled, endMonth, errorsDisplayType = 'bellow', label, locale = fr, optional = false, overlayOnMobile = false, placeholder = 'Sélectionner une date', startMonth, }) {
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    const [open, setOpen] = React.useState(false);
    const isMobile = useIsMobile();
    const useOverlay = overlayOnMobile && isMobile;
    // Default disabled logic: dates before today
    const defaultDisabled = React.useCallback((date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    }, []);
    const disabledDates = disabled ?? defaultDisabled;
    const handleSelect = (date) => {
        if (date) {
            field.handleChange(date);
            setOpen(false);
        }
        else if (optional) {
            field.handleChange(null);
            setOpen(false);
        }
    };
    const triggerButton = (props) => (_jsxs(Button, { "aria-invalid": errors.length > 0, className: cn('data-[empty=true]:text-muted-foreground relative justify-start text-left font-normal', 'aria-invalid:ring-destructive/20 aria-invalid:border-destructive', className), "data-empty": !field.state.value, onClick: props?.onClick, type: "button", variant: "outline", children: [errorsDisplayType === 'popover' && _jsx(FieldError, { asPopover: true, errors: errors }), _jsx(CalendarIcon, { color: "rgba(65, 70, 81, 1)" }), field.state.value &&
                field.state.value instanceof Date &&
                !isNaN(field.state.value.getTime()) ? (_jsx("span", { className: "text-gray-light-600", children: format(field.state.value, dateFormat, { locale }) })) : (_jsx("span", { className: "text-gray-light-600", children: placeholder }))] }));
    if (useOverlay) {
        return (_jsxs("div", { className: "flex flex-1 flex-col gap-2", children: [label && (_jsx(OptionalLabel, { "aria-invalid": errors.length > 0, label: label, optional: optional })), triggerButton({ onClick: () => setOpen(true) }), errorsDisplayType === 'bellow' && _jsx(FieldError, { errors: errors }), _jsx(FullScreenOverlay, { onOpenChange: setOpen, open: open, title: label ?? placeholder, children: _jsx(MobileScrollCalendar, { disabled: disabledDates, mode: "single", onSelect: handleSelect, selected: field.state.value ?? undefined }) })] }));
    }
    return (_jsxs("div", { className: "flex flex-1 flex-col gap-2", children: [label && (_jsx(OptionalLabel, { "aria-invalid": errors.length > 0, label: label, optional: optional })), _jsxs(Popover, { modal: true, onOpenChange: (isOpen) => {
                    setOpen(isOpen);
                    // Clear the validation error popover so it doesn't overlap the calendar.
                    // setErrorMap merges, so each source must be set to undefined explicitly.
                    if (isOpen) {
                        field.setErrorMap({
                            onBlur: undefined,
                            onChange: undefined,
                            onMount: undefined,
                            onSubmit: undefined,
                        });
                    }
                }, open: open, children: [_jsx(PopoverTrigger, { asChild: true, children: triggerButton() }), _jsx(PopoverContent, { className: "w-auto p-4", children: _jsx(Calendar, { captionLayout: captionLayout, defaultMonth: field.state.value ?? undefined, disabled: disabledDates, endMonth: endMonth, locale: locale, mode: "single", onSelect: handleSelect, required: !optional, selected: field.state.value ?? undefined, startMonth: startMonth }) })] }), errorsDisplayType === 'bellow' && _jsx(FieldError, { errors: errors })] }));
}
